import { Component } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormBuilder, FormGroup, Validators, ReactiveFormsModule } from '@angular/forms';
import { RouterModule } from '@angular/router';
import { AuthService } from '../../services/auth.service';

@Component({
  selector: 'app-forgot-password',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule, RouterModule],
  template: `
    <div class="min-vh-100 d-flex align-items-center justify-content-center" style="background-color: #f5f5f5; padding: 20px;">
      <div class="card" style="width: 100%; max-width: 450px; box-shadow: 0 4px 6px rgba(0,0,0,0.1);">
        <div class="card-header text-center" style="background-color: #007bff; color: white;">
          <h3 style="margin: 0; padding: 15px 0;">Reset Password</h3>
          <p class="mb-0" style="font-size: 0.9rem; opacity: 0.9;">Reset your password using OTP</p>
        </div>
        <div class="card-body" style="padding: 30px;">
          <!-- Step 1: Request OTP -->
          <div *ngIf="step === 1">
            <form [formGroup]="requestOtpForm" (ngSubmit)="onRequestOtp()">
              <div class="form-group mb-3">
                <label class="form-label" style="font-weight: 500;">Mobile Number</label>
                <input 
                  type="tel" 
                  class="form-control" 
                  formControlName="mobileNumber"
                  [class.is-invalid]="requestOtpForm.get('mobileNumber')?.invalid && requestOtpForm.get('mobileNumber')?.touched"
                  placeholder="Enter your mobile number"
                  maxlength="15">
                <div class="invalid-feedback" *ngIf="requestOtpForm.get('mobileNumber')?.invalid && requestOtpForm.get('mobileNumber')?.touched">
                  <span *ngIf="requestOtpForm.get('mobileNumber')?.errors?.['required']">Mobile number is required</span>
                  <span *ngIf="requestOtpForm.get('mobileNumber')?.errors?.['pattern']">Please enter a valid mobile number</span>
                </div>
                <small class="form-text text-muted">Enter the mobile number registered with your account</small>
              </div>

              <div class="form-group">
                <button 
                  type="submit" 
                  class="btn btn-primary w-100"
                  style="padding: 10px; font-weight: 500;"
                  [disabled]="requestOtpForm.invalid || isLoading">
                  <span *ngIf="isLoading">Sending OTP...</span>
                  <span *ngIf="!isLoading">Send OTP</span>
                </button>
              </div>
            </form>
          </div>

          <!-- Step 2: Verify OTP and Set New Password -->
          <div *ngIf="step === 2">
            <div class="alert alert-info mb-3" style="font-size: 0.9rem;">
              <strong>OTP Sent!</strong> Please check your mobile number <strong>{{ mobileNumber }}</strong> for the 6-digit OTP code.
            </div>

            <form [formGroup]="verifyOtpForm" (ngSubmit)="onVerifyOtp()">
              <div class="form-group mb-3">
                <label class="form-label" style="font-weight: 500;">OTP Code</label>
                <input 
                  type="text" 
                  class="form-control text-center" 
                  formControlName="otpCode"
                  [class.is-invalid]="verifyOtpForm.get('otpCode')?.invalid && verifyOtpForm.get('otpCode')?.touched"
                  placeholder="Enter 6-digit OTP"
                  maxlength="6"
                  style="font-size: 1.2rem; letter-spacing: 0.5rem; font-weight: 600;">
                <div class="invalid-feedback" *ngIf="verifyOtpForm.get('otpCode')?.invalid && verifyOtpForm.get('otpCode')?.touched">
                  <span *ngIf="verifyOtpForm.get('otpCode')?.errors?.['required']">OTP code is required</span>
                  <span *ngIf="verifyOtpForm.get('otpCode')?.errors?.['pattern']">OTP must be 6 digits</span>
                </div>
              </div>

              <div class="form-group mb-3">
                <label class="form-label" style="font-weight: 500;">New Password</label>
                <input 
                  type="password" 
                  class="form-control" 
                  formControlName="newPassword"
                  [class.is-invalid]="verifyOtpForm.get('newPassword')?.invalid && verifyOtpForm.get('newPassword')?.touched"
                  placeholder="Enter new password">
                <div class="invalid-feedback" *ngIf="verifyOtpForm.get('newPassword')?.invalid && verifyOtpForm.get('newPassword')?.touched">
                  <span *ngIf="verifyOtpForm.get('newPassword')?.errors?.['required']">Password is required</span>
                  <span *ngIf="verifyOtpForm.get('newPassword')?.errors?.['minlength']">Password must be at least 6 characters</span>
                </div>
              </div>

              <div class="form-group mb-3">
                <label class="form-label" style="font-weight: 500;">Confirm Password</label>
                <input 
                  type="password" 
                  class="form-control" 
                  formControlName="confirmPassword"
                  [class.is-invalid]="verifyOtpForm.get('confirmPassword')?.invalid && verifyOtpForm.get('confirmPassword')?.touched"
                  placeholder="Confirm new password">
                <div class="invalid-feedback" *ngIf="verifyOtpForm.get('confirmPassword')?.invalid && verifyOtpForm.get('confirmPassword')?.touched">
                  <span *ngIf="verifyOtpForm.get('confirmPassword')?.errors?.['required']">Please confirm your password</span>
                  <span *ngIf="verifyOtpForm.get('confirmPassword')?.errors?.['passwordMismatch']">Passwords do not match</span>
                </div>
              </div>

              <div class="form-group mb-3">
                <button 
                  type="submit" 
                  class="btn btn-success w-100"
                  style="padding: 10px; font-weight: 500;"
                  [disabled]="verifyOtpForm.invalid || isLoading">
                  <span *ngIf="isLoading">Resetting Password...</span>
                  <span *ngIf="!isLoading">Reset Password</span>
                </button>
              </div>

              <div class="form-group text-center">
                <button 
                  type="button" 
                  class="btn btn-link text-decoration-none"
                  (click)="backToStep1()"
                  [disabled]="isLoading">
                  ← Back to Mobile Number
                </button>
              </div>
            </form>
          </div>

          <!-- Step 3: Success -->
          <div *ngIf="step === 3" class="text-center">
            <div class="mb-4">
              <svg width="64" height="64" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" style="color: #28a745;">
                <path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41L9 16.17z" fill="currentColor"/>
              </svg>
            </div>
            <h4 class="text-success mb-3">Password Reset Successful!</h4>
            <p class="text-muted mb-4">Your password has been reset successfully. You can now login with your new password.</p>
            <a routerLink="/login" class="btn btn-primary">Go to Login</a>
          </div>

          <!-- Error Messages -->
          <div class="alert alert-danger mt-3" *ngIf="errorMessage" style="font-size: 0.9rem;">
            {{ errorMessage }}
          </div>

          <!-- Success Messages -->
          <div class="alert alert-success mt-3" *ngIf="successMessage && step !== 3" style="font-size: 0.9rem;">
            {{ successMessage }}
          </div>
        </div>
        <div class="card-footer text-center" style="background-color: #f8f9fa;">
          <p class="mb-0">
            <a routerLink="/login" class="text-decoration-none" style="color: #007bff;">Back to Login</a>
          </p>
        </div>
      </div>
    </div>
  `,
  styles: [`
    .text-decoration-none {
      text-decoration: none;
    }
    
    .text-decoration-none:hover {
      text-decoration: underline;
    }

    .form-control:focus {
      border-color: #007bff;
      box-shadow: 0 0 0 0.2rem rgba(0, 123, 255, 0.25);
    }

    .btn-link {
      color: #007bff;
    }

    .btn-link:hover {
      text-decoration: underline;
    }
  `]
})
export class ForgotPasswordComponent {
  requestOtpForm: FormGroup;
  verifyOtpForm: FormGroup;
  step = 1; // 1: Request OTP, 2: Verify OTP, 3: Success
  isLoading = false;
  errorMessage = '';
  successMessage = '';
  mobileNumber = '';

  constructor(
    private fb: FormBuilder,
    private authService: AuthService
  ) {
    // Form for requesting OTP
    this.requestOtpForm = this.fb.group({
      mobileNumber: ['', [
        Validators.required,
        Validators.pattern(/^[+]?[(]?[0-9]{1,4}[)]?[-\s.]?[(]?[0-9]{1,4}[)]?[-\s.]?[0-9]{1,9}$/)
      ]]
    });

    // Form for verifying OTP and setting new password
    this.verifyOtpForm = this.fb.group({
      otpCode: ['', [
        Validators.required,
        Validators.pattern(/^\d{6}$/)
      ]],
      newPassword: ['', [
        Validators.required,
        Validators.minLength(6)
      ]],
      confirmPassword: ['', [Validators.required]]
    }, { validators: this.passwordMatchValidator });
  }

  passwordMatchValidator(form: FormGroup) {
    const newPassword = form.get('newPassword');
    const confirmPassword = form.get('confirmPassword');
    
    if (newPassword && confirmPassword && newPassword.value !== confirmPassword.value) {
      confirmPassword.setErrors({ passwordMismatch: true });
      return { passwordMismatch: true };
    }
    return null;
  }

  onRequestOtp(): void {
    if (this.requestOtpForm.valid) {
      this.isLoading = true;
      this.errorMessage = '';
      this.successMessage = '';

      this.mobileNumber = this.requestOtpForm.value.mobileNumber;

      this.authService.requestPasswordResetOtp(this.mobileNumber).subscribe({
        next: (response) => {
          this.isLoading = false;
          if (response.success) {
            this.successMessage = 'OTP has been sent to your mobile number.';
            this.step = 2;
            // Clear the form
            this.requestOtpForm.reset();
          } else {
            this.errorMessage = response.message || 'Failed to send OTP. Please try again.';
          }
        },
        error: (error) => {
          this.isLoading = false;
          this.errorMessage = error.error?.message || 'Failed to send OTP. Please check your mobile number and try again.';
        }
      });
    }
  }

  onVerifyOtp(): void {
    if (this.verifyOtpForm.valid) {
      this.isLoading = true;
      this.errorMessage = '';
      this.successMessage = '';

      const otpCode = this.verifyOtpForm.value.otpCode;
      const newPassword = this.verifyOtpForm.value.newPassword;

      this.authService.verifyOtpAndResetPassword(this.mobileNumber, otpCode, newPassword).subscribe({
        next: (response) => {
          this.isLoading = false;
          if (response.success) {
            this.step = 3;
            this.successMessage = '';
            // Clear forms
            this.verifyOtpForm.reset();
          } else {
            this.errorMessage = response.message || 'Failed to reset password. Please try again.';
          }
        },
        error: (error) => {
          this.isLoading = false;
          this.errorMessage = error.error?.message || error.error?.errors?.[0] || 'Failed to reset password. Please check your OTP and try again.';
        }
      });
    }
  }

  backToStep1(): void {
    this.step = 1;
    this.errorMessage = '';
    this.successMessage = '';
    this.verifyOtpForm.reset();
  }
}
